import classNames from 'classnames'
import { useState } from 'react'
import { useWatch } from 'react-hook-form'

import AtomCheckbox from '@/components/Atoms/AtomCheckbox/AtomCheckbox'
import AtomCollapse from '@/components/Atoms/AtomCollapse/AtomCollapse'
import AtomNotice from '@/components/Atoms/AtomNotice/AtomNotice'
import Loader from '@/components/Atoms/Loader/Loader'

import { labelWithTooltip } from '../../RequiredLabel/labelWithTooltip'
import cn from '../ConfigModal.module.scss'
import type { ConfigPriceFormApi } from '../hooks/useConfigPriceForm'
import CurrencyFields from './CurrencyFields'
import { PriceCheckboxField, PriceTextField } from './PriceFields'
import PriceGuardsFields from './PriceGuardsFields'
import pricesCn from './PricesStep.module.scss'
import SegmentRuleRow from './SegmentRuleRow'

interface PricesStepProps {
  hidden: boolean
  prices: ConfigPriceFormApi
  isOrganisationSelected: boolean
}

function PricesStep({
  hidden,
  prices,
  isOrganisationSelected,
}: PricesStepProps) {
  const { control, formState, setValue } = prices.form
  const [collapseKey, setCollapseKey] = useState(0)
  const { context } = prices
  const [segments, currencyFromFile] = useWatch({
    control,
    name: ['segments', 'currencyFromFile'],
  })

  const toggleAllSegments = (checked: boolean) => {
    setValue(
      'segments',
      segments.map((row) => ({ ...row, enabled: checked })),
      { shouldDirty: true, shouldValidate: formState.isSubmitted }
    )
    if (checked) setCollapseKey((key) => key + 1)
  }

  return (
    <div
      className={classNames(cn.step, hidden && cn.stepHidden)}
      aria-hidden={hidden}
    >
      <h3 className={cn.sectionTitle}>Закупочная цена</h3>
      <div className={cn.grid}>
        <PriceTextField
          context={context}
          name="purchaseDiscountPercent"
          title={labelWithTooltip(
            'Скидка к закупке, %',
            'Отрицательная скидка — надбавка, например на логистику'
          )}
        />
      </div>
      {currencyFromFile && (
        <>
          <h3 className={cn.sectionTitle}>Валюта</h3>
          <CurrencyFields context={context} />
        </>
      )}
      <h3 className={cn.sectionTitle}>
        Сегменты
        {segments.length > 0 && (
          <span className={pricesCn.selectAll}>
            <AtomCheckbox
              checked={segments.every((row) => row.enabled)}
              onChange={toggleAllSegments}
              isOrganisationSelected={isOrganisationSelected}
            >
              Выбрать все
            </AtomCheckbox>
          </span>
        )}
      </h3>
      {prices.segmentsQuery.isPending && <Loader />}
      {prices.segmentsQuery.isError && (
        <AtomNotice type="error">Не удалось загрузить сегменты</AtomNotice>
      )}
      {formState.errors.segments?.message && (
        <span role="alert" className={pricesCn.error}>
          {formState.errors.segments.message}
        </span>
      )}
      <AtomCollapse
        key={collapseKey}
        isOrganisationSelected={isOrganisationSelected}
        items={segments.map((row, index) => ({
          key: row.segmentId,
          label: (
            <span
              className={pricesCn.segmentTitle}
              onClick={(event) => event.stopPropagation()}
            >
              <PriceCheckboxField
                context={context}
                name={`segments.${index}.enabled`}
              >
                {row.description}
              </PriceCheckboxField>
            </span>
          ),
          children: row.enabled ? (
            <SegmentRuleRow context={context} index={index} />
          ) : undefined,
        }))}
      />
      <h3 className={cn.sectionTitle}>Фильтры и правила</h3>
      <PriceGuardsFields context={context} />
    </div>
  )
}

export default PricesStep
